import { Suspense, useEffect } from 'react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import { useNavigate, useParams } from 'react-router-dom';
import { LocalErrorBoundary } from 'components/ErrorBoundary';

import Loading from 'components/Loading';
import BookkeeperLocationForm from './components/BookkeeperLocationForm/BookkeeperLocationForm';
import BookkeeperTemplateForm from './components/BookkeeperTemplateForm';
import BookkeeperUsersForm from './components/BookkeeperUsersForm/BookkeeperUsersForm';
import BookkeeperEmailWhitelist from './components/BookkeeperEmailWhitelist';

import { abilityCan } from 'lib/utils';
import HoneLocationsProvider from '../../../components/HoneLocations';

import BookkeeperReportingConfig from './components/BookkeeperReportingConfig';
import useLocationRouting from 'hooks/useLocationRouting';

import { useAuth, useAuthContext } from 'context/useAuthContext';
import { HoneAbilityActions, HoneAbilitySubjects } from '@hone-automation/common';
import { useFlags } from 'launchdarkly-react-client-sdk';

import BookkeeperAccrualsSettingsForm from 'presentation/pages/bookkeeper/components/BookkeeperAccrualsSettingsForm';
import { useQueryState } from 'hooks/useQueryState';
import BookkeeperSlackChannels from './components/BookkeeperSlackChannels';
import NewLayout from '../../components/NewLayout/NewLayout';
import QboSettings from '../../components/CoAEditor/QboSettings';
import BookkeeperSetupCoA from './components/BookkeeperSetupCoA';

import { MENU_ICONS } from '../../../constants';
import BookkeeperBudgets from './components/BookkeperBudgets/BookkeeperBudgets';
import { useLocationsStore } from 'hooks/useLocationsStore';
import Footer from 'presentation/components/Footer/Footer';
import BookkeeperBSAudit from './components/BookkeeperBSAudit/BookkeeperBSAudit';

function Bookkeeper(): JSX.Element {
  useLocationRouting(false);
  const navigateTo = useNavigate();
  const [activeTab] = useQueryState('activetab', 'General');

  const { locationId } = useParams();
  const { fetchLocationById } = useLocationsStore();

  useEffect(() => {
    if (locationId && activeTab === 'General') {
      fetchLocationById(locationId);
    }
  }, [activeTab]);

  const { userInfoHasLoaded, currentLocation, currentLocationAbilities } = useAuthContext();
  const { enableEmailWhitelist } = useFlags();
  const isBookkeeper = abilityCan(
    currentLocationAbilities,
    HoneAbilityActions.read,
    HoneAbilitySubjects.Bookkeeper_Controls
  );

  const tabs = [
    { name: 'General', component: <BookkeeperLocationForm /> },
    {
      name: 'Setup CoA',
      component: <BookkeeperSetupCoA />,
      screenSettings: <QboSettings />,
    },
    { name: 'Accruals', component: <BookkeeperAccrualsSettingsForm /> },
    { name: 'Users', component: <BookkeeperUsersForm /> },
    ...(enableEmailWhitelist ? [{ name: 'Email Whitelist', component: <BookkeeperEmailWhitelist /> }] : []),
    { name: 'Templates', component: <BookkeeperTemplateForm /> },
    { name: 'BS Audit', component: <BookkeeperBSAudit /> },
    { name: 'Reporting Config', component: <BookkeeperReportingConfig /> },
    { name: 'Budgets', component: <BookkeeperBudgets /> },
    { name: 'Slack Channels', component: <BookkeeperSlackChannels /> },
  ];

  useEffect(() => {
    const currentAbilitiesHasBeenLoaded = currentLocationAbilities.length > 0;
    if (!userInfoHasLoaded) return;
    if (!currentAbilitiesHasBeenLoaded) return;
    if (isBookkeeper) return;
    navigateTo('/app');
  }, [userInfoHasLoaded, isBookkeeper, currentLocationAbilities, history]);

  const handleClickTab = (tabName: string) => {
    navigateTo(`/app/location/${currentLocation?.locationId}/bookkeeper?activetab=${tabName}`);
  };

  const idxActiveTab = tabs.findIndex(tab => tab.name === activeTab);

  return (
    <Tabs selectedTabClassName="react-tabs-tab-selected" selectedIndex={Number(idxActiveTab)}>
      <NewLayout
        icon={MENU_ICONS.Bookkeeper}
        pageTitle="Accountant"
        className="bookkeeper"
        screenSettings={tabs[idxActiveTab].screenSettings}
        chartArea={
          <TabList>
            {tabs.map((tab, idx: number) => (
              <Tab
                selected={idx === idxActiveTab}
                className="react-tabs-tab"
                onClick={() => handleClickTab(tab.name)}
                key={`${tab.name}_${idx}`}
              >
                {tab.name}
              </Tab>
            ))}
          </TabList>
        }
      >
        <div className="bookkeeper-layout">
          <div className="Layout">
            <Suspense fallback={<Loading />}>
              <main className="Layout-main">
                <div className="Layout-content">
                  <HoneLocationsProvider>
                    {tabs.map((tab, idx: number) => (
                      <TabPanel key={`${tab.name}_${idx}`}>{tab.component}</TabPanel>
                    ))}
                  </HoneLocationsProvider>
                </div>
                <Footer />
              </main>
            </Suspense>
          </div>
        </div>
      </NewLayout>
    </Tabs>
  );
}

const BookkeeperWrapped = () => (
  <LocalErrorBoundary>
    <Bookkeeper />
  </LocalErrorBoundary>
);

export default BookkeeperWrapped;
